/**
 * @package FlorenceEGI — EGI — Home Supreme Impact Metric renderer
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Island 4 — counter 220px tabular-nums animato on intersection +
 *          lede manifesto + 3 EPP cards (travertino) + manifesto quote finale.
 *          Consuma /api/home-supreme/impact.
 */

import { el, clear } from "./utils";
import { buildPlate, pickTone } from "./plate";

type ImpactDto = {
    metrics: { plastic_recovered_kg: number; sample?: boolean };
    sample_notice?: string;
    epps: { id: number; name: string; description: string; url: string }[];
};

const fmt = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const prefersReduced = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function animateCounter(node: HTMLElement, target: number, duration = 1600): void {
    if (prefersReduced() || target <= 0) {
        node.textContent = fmt.format(target);
        return;
    }
    const start = performance.now();
    const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const v = Math.round(target * eased);
        node.textContent = fmt.format(v);
        if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
}

function eppCard(epp: ImpactDto["epps"][number]): HTMLElement {
    const card = el("article", { className: "fe-epp" });

    const media = el("div", { className: "fe-epp__media" });
    const plate = buildPlate(`epp-${epp.id}`, pickTone(`epp-${epp.id}`));
    media.appendChild(plate);
    media.appendChild(el("span", { className: "fe-epp__tag", text: "EPP · Verificato" }));
    card.appendChild(media);

    const body = el("div", { className: "fe-epp__body" });
    body.appendChild(el("div", { className: "fe-epp__place", text: "Progetto ambientale" }));
    body.appendChild(el("h3", { className: "fe-epp__title", text: epp.name }));
    if (epp.description) body.appendChild(el("p", { text: epp.description }));

    const cta = el("a", {
        className: "fe-epp__cta",
        attrs: { href: epp.url, "aria-label": `Visita il progetto ${epp.name}` },
    });
    cta.appendChild(document.createTextNode("Visita il progetto"));
    const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrow.setAttribute("width", "12");
    arrow.setAttribute("height", "8");
    arrow.setAttribute("viewBox", "0 0 12 8");
    arrow.setAttribute("fill", "none");
    arrow.setAttribute("aria-hidden", "true");
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", "M1 4h10M8 1l3 3-3 3");
    p.setAttribute("stroke", "currentColor");
    p.setAttribute("stroke-width", "1");
    p.setAttribute("stroke-linecap", "round");
    p.setAttribute("stroke-linejoin", "round");
    arrow.appendChild(p);
    cta.appendChild(arrow);
    body.appendChild(cta);

    card.appendChild(body);
    return card;
}

export function renderImpactMetric(host: HTMLElement, data: Record<string, unknown>): void {
    clear(host);
    const d = data as ImpactDto;
    const kg = Math.round(d?.metrics?.plastic_recovered_kg ?? 0);

    const wrap = el("div", { className: "fe-impact" });

    // Metric
    const metric = el("div", { className: "fe-impact__metric" });
    const numWrap = el("div");
    const number = el("span", {
        className: "fe-impact__number",
        attrs: { "aria-live": "polite" },
    });
    number.textContent = "0";
    numWrap.appendChild(number);
    numWrap.appendChild(el("span", { className: "fe-impact__unit", text: " kg" }));
    // Sample-data disclaimer (CEO constraint: mai dati falsi senza label esplicita)
    if (d?.metrics?.sample && d.sample_notice) {
        numWrap.appendChild(el("span", {
            className: "fe-impact__sample",
            text: d.sample_notice,
            attrs: { role: "note" },
        }));
    }
    metric.appendChild(numWrap);

    const caption = el("div", { className: "fe-impact__caption" });
    const lede = el("p", { className: "fe-impact__lede" });
    lede.append(
        document.createTextNode("di plastica "),
        el("em", { text: "restituiti" }),
        document.createTextNode(" al Mediterraneo nell'ultimo ciclo. Ogni EGI acquistato finanzia progetti EPP verificati."),
    );
    caption.appendChild(lede);
    caption.appendChild(el("div", {
        className: "fe-impact__audit",
        text: "Audit · Blockchain\nVerificato · On-chain",
    }));
    metric.appendChild(caption);
    wrap.appendChild(metric);

    // EPP cards
    const list = el("div", { className: "fe-impact__epps", attrs: { role: "list" } });
    (d?.epps ?? []).slice(0, 3).forEach((epp) => {
        const card = eppCard(epp);
        card.setAttribute("role", "listitem");
        list.appendChild(card);
    });
    wrap.appendChild(list);

    // Manifesto quote
    const manifesto = el("div", { className: "fe-impact__manifesto" });
    const quote = el("p", { className: "fe-impact__manifesto-quote" });
    quote.append(
        document.createTextNode("«Acquistare è "),
        el("em", { text: "restituire" }),
        document.createTextNode(". Ogni opera è un patto con il pianeta.»"),
    );
    manifesto.appendChild(quote);
    wrap.appendChild(manifesto);

    host.appendChild(wrap);

    // Trigger counter animation on intersection
    if (typeof IntersectionObserver !== "undefined") {
        const io = new IntersectionObserver((entries, obs) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    animateCounter(number, kg);
                    obs.disconnect();
                    break;
                }
            }
        }, { threshold: 0.3 });
        io.observe(number);
    } else {
        animateCounter(number, kg);
    }
}
